import type { TestCaseListItem } from "@/types";

/**
 * Whether a TC's origin — where its 👎 rows live — could be read (bound SAMS).
 *
 * "Unavailable" and "deleted" are different answers with different next
 * steps: a deleted source is gone for good, an unavailable one was simply not
 * read this time. BE only sends `feedback_count: null` together with a
 * `source_load_error`, so either signal is enough; checking both keeps a
 * partial response from reading as a real zero.
 */
export function isSourceUnavailable(
  tc: Pick<TestCaseListItem, "feedback_count" | "warnings" | "source_load_error">,
): boolean {
  return (
    Boolean(tc.source_load_error) ||
    tc.feedback_count === null ||
    tc.warnings.includes("source_unavailable")
  );
}

export const SOURCE_UNAVAILABLE_LABEL = "원본 확인 불가";

/**
 * The origin was read and the 👎 rows are gone (message deleted). TCs are kept
 * permanently, so this is information, never a gate: what the caller may do is
 * still `management_capabilities`. Only BE's explicit warning counts — an
 * unread origin (`isSourceUnavailable`) is never reported as deleted.
 */
export function isSourceDeleted(
  tc: Pick<TestCaseListItem, "feedback_count" | "warnings" | "source_load_error">,
): boolean {
  return tc.warnings.includes("source_feedback_deleted") && !isSourceUnavailable(tc);
}

export const SOURCE_DELETED_LABEL = "원본 삭제됨";

export const SOURCE_DELETED_TEXT =
  "원본 메시지가 삭제되어 원본 👎 기록은 더 이상 읽을 수 없습니다. TC 는 영구 보존되며, 이 TC 에 보관된 증거와 실행 결과는 그대로 쓰입니다.";

/** One sentence for the person reading the TC; the code stays out of it. */
export function sourceUnavailableText(code: string | null | undefined): string {
  if (code === "SOURCE_ENVIRONMENT_UNKNOWN") {
    return "원본이 어느 환경에 있는지 알 수 없어 원본 👎 기록을 확인하지 못했습니다. 삭제 여부도 확인할 수 없습니다.";
  }
  return "지금은 원본 👎 기록을 불러올 수 없습니다. 삭제 여부는 확인할 수 없으니 잠시 후 다시 확인해 주세요.";
}

/** A null count is "not known", never 0 — say so instead of printing it. */
export function feedbackCountText(count: number | null): string {
  return count === null ? "확인 불가" : `${count} 건`;
}

/**
 * Most 👎 first. Rows whose count is unknown go after every known count —
 * including real zeros — since ranking them as 0 would claim the source was
 * checked and found empty. Ties return 0, so the caller's tie-break keeps the
 * order deterministic.
 */
export function compareFeedbackCountDesc(
  a: Pick<TestCaseListItem, "feedback_count">,
  b: Pick<TestCaseListItem, "feedback_count">,
): number {
  if (a.feedback_count === null || b.feedback_count === null) {
    if (a.feedback_count === b.feedback_count) return 0;
    return a.feedback_count === null ? 1 : -1;
  }
  return b.feedback_count - a.feedback_count;
}
